/** Formatting helpers — Indian number system for prices, human dates. */

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const inrPlainFormatter = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 0,
});

/** Format a price in Indian rupees, e.g. 125000 → "₹1,25,000". null → "—". */
export function formatINR(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return inrFormatter.format(value);
}

/** Indian-grouped plain number, e.g. 1250000 → "12,50,000". */
export function formatIndianNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return inrPlainFormatter.format(value);
}

const dateTimeFmt = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const dateFmt = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

/** Epoch ms → "18 Jul 2026, 03:45 pm". Falsy → "—". */
export function formatDateTime(ts: number | null | undefined): string {
  if (!ts) return '—';
  return dateTimeFmt.format(new Date(ts));
}

/** Epoch ms → "18 Jul 2026". Falsy → "—". */
export function formatDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  return dateFmt.format(new Date(ts));
}

/** Relative "time ago" for feeds. */
export function timeAgo(ts: number | null | undefined): string {
  if (!ts) return '—';
  const diff = Date.now() - ts;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return formatDate(ts);
}

/** Milliseconds → "1h 23m" / "4m 05s" / "12s". */
export function formatDuration(ms: number): string {
  if (ms <= 0 || Number.isNaN(ms)) return '0s';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/** Number of whole days since a timestamp; Infinity when never active. */
export function daysSince(ts: number | null | undefined): number {
  if (!ts) return Infinity;
  return Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
}
